import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { Form, FormGroup, Label, Input, Button, Container, Row, Col, Card, CardBody, CardHeader, Alert, InputGroup, InputGroupAddon } from 'reactstrap';
import { useDispatch } from 'react-redux';
import { updateTitle } from '../../redux/updatePageHeadeSlice';
import changePassword from '../../api/changePassword';
import './styles.css';

const ChangePassword = () => {
    const [formData, setFormData] = useState({
        oldPassword: '',
        newPassword: '',
        confirmPassword: ''
    });
    
    const [showPasswords, setShowPasswords] = useState({
        oldPassword: false,
        newPassword: false,
        confirmPassword: false
    });
    
    const [errors, setErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    
    const history = useHistory();
    const dispatch = useDispatch();

    // Mettre à jour le titre du header
    useEffect(() => {
        dispatch(updateTitle('Changer mot de passe'));
    }, [dispatch]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        
        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const togglePasswordVisibility = (field) => {
        setShowPasswords(prev => ({
            ...prev,
            [field]: !prev[field]
        }));
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.oldPassword.trim()) {
            newErrors.oldPassword = 'Le mot de passe actuel est requis';
        }

        if (!formData.newPassword.trim()) {
            newErrors.newPassword = 'Le nouveau mot de passe est requis';
        } else if (formData.newPassword.length < 5) {
            newErrors.newPassword = 'Le nouveau mot de passe doit contenir au moins 5 caractères';
        }

        if (!formData.confirmPassword.trim()) {
            newErrors.confirmPassword = 'La confirmation du mot de passe est requise';
        } else if (formData.newPassword !== formData.confirmPassword) {
            newErrors.confirmPassword = 'Les mots de passe ne correspondent pas';
        }

        if (formData.oldPassword === formData.newPassword) {
            newErrors.newPassword = 'Le nouveau mot de passe doit être différent de l\'actuel';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }

        setIsLoading(true);
        setSuccessMessage('');
        setErrorMessage('');

        try {
            const response = await changePassword(
                formData.oldPassword,
                formData.newPassword,
                formData.confirmPassword
            );
            
            if (response.status === 200) {
                setSuccessMessage('Mot de passe modifié avec succès !');
                
                // Rediriger vers la page d'accueil après 2 secondes
                setTimeout(() => {
                    history.push('/home');
                }, 2000);
            }
            
        } catch (error) {
            console.error('Erreur API:', error);
            if (error.response?.data?.message) {
                setErrorMessage(error.response.data.message);
            } else if (error.response?.data?.errorMessage) {
                setErrorMessage(error.response.data.errorMessage);
            } else {
                setErrorMessage('Une erreur est survenue lors du changement de mot de passe');
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleCancel = () => {
        history.goBack();
    };

    return (
        <div className="change-password-container">
            <div className="row justify-content-center">
                <div className="col-md-8 col-lg-6 col-xl-5">
                    <Card className="mt-4 change-password-card">
                        <CardHeader className="text-center">
                            <h4 className="mb-0">Changer le mot de passe</h4>
                        </CardHeader>
                        <CardBody className="p-4">
                            {successMessage && (
                                <Alert color="success" className="mb-3">
                                    {successMessage}
                                </Alert>
                            )}
                            
                            {errorMessage && (
                                <Alert color="danger" className="mb-3">
                                    {errorMessage}
                                </Alert>
                            )}

                            <Form onSubmit={handleSubmit}>
                                <FormGroup>
                                    <Label for="oldPassword">
                                        Mot de passe actuel <span className="text-danger">*</span>
                                    </Label>
                                    <InputGroup className="password-input-group">
                                        <Input
                                            type={showPasswords.oldPassword ? 'text' : 'password'}
                                            name="oldPassword"
                                            id="oldPassword"
                                            value={formData.oldPassword}
                                            onChange={handleInputChange}
                                            invalid={!!errors.oldPassword}
                                            placeholder="Entrez votre mot de passe actuel"
                                            className="password-input"
                                        />
                                        <InputGroupAddon addonType="append">
                                            <Button
                                                type="button"
                                                color="outline-secondary"
                                                onClick={() => togglePasswordVisibility('oldPassword')}
                                                size="sm"
                                                className="password-toggle-btn"
                                            >
                                                {showPasswords.oldPassword ? 'Masquer' : 'Voir'}
                                            </Button>
                                        </InputGroupAddon>
                                    </InputGroup>
                                    {errors.oldPassword && (
                                        <div className="text-danger small mt-1">{errors.oldPassword}</div>
                                    )}
                                </FormGroup>

                                <FormGroup>
                                    <Label for="newPassword">
                                        Nouveau mot de passe <span className="text-danger">*</span>
                                    </Label>
                                    <InputGroup className="password-input-group">
                                        <Input
                                            type={showPasswords.newPassword ? 'text' : 'password'}
                                            name="newPassword"
                                            id="newPassword"
                                            value={formData.newPassword}
                                            onChange={handleInputChange}
                                            invalid={!!errors.newPassword}
                                            placeholder="Entrez votre nouveau mot de passe"
                                            className="password-input"
                                        />
                                        <InputGroupAddon addonType="append">
                                            <Button
                                                type="button"
                                                color="outline-secondary"
                                                onClick={() => togglePasswordVisibility('newPassword')}
                                                size="sm"
                                                className="password-toggle-btn"
                                            >
                                                {showPasswords.newPassword ? 'Masquer' : 'Voir'}
                                            </Button>
                                        </InputGroupAddon>
                                    </InputGroup>
                                    {errors.newPassword && (
                                        <div className="text-danger small mt-1">{errors.newPassword}</div>
                                    )}
                                    <small className="form-text text-muted">
                                        Le mot de passe doit contenir au moins 5 caractères.
                                    </small>
                                </FormGroup>

                                <FormGroup>
                                    <Label for="confirmPassword">
                                        Confirmer le nouveau mot de passe <span className="text-danger">*</span>
                                    </Label>
                                    <InputGroup className="password-input-group">
                                        <Input
                                            type={showPasswords.confirmPassword ? 'text' : 'password'}
                                            name="confirmPassword"
                                            id="confirmPassword"
                                            value={formData.confirmPassword}
                                            onChange={handleInputChange}
                                            invalid={!!errors.confirmPassword}
                                            placeholder="Confirmez votre nouveau mot de passe"
                                            className="password-input"
                                        />
                                        <InputGroupAddon addonType="append">
                                            <Button
                                                type="button"
                                                color="outline-secondary"
                                                onClick={() => togglePasswordVisibility('confirmPassword')}
                                                size="sm"
                                                className="password-toggle-btn"
                                            >
                                                {showPasswords.confirmPassword ? 'Masquer' : 'Voir'}
                                            </Button>
                                        </InputGroupAddon>
                                    </InputGroup>
                                    {errors.confirmPassword && (
                                        <div className="text-danger small mt-1">{errors.confirmPassword}</div>
                                    )}
                                </FormGroup>

                                <div className="d-flex gap-2 mt-4">
                                    <Button
                                        type="submit"
                                        color="primary"
                                        className="flex-fill"
                                        disabled={isLoading}
                                    >
                                        {isLoading ? 'Modification...' : 'Modifier le mot de passe'}
                                    </Button>
                                    <Button
                                        type="button"
                                        color="secondary"
                                        onClick={handleCancel}
                                        disabled={isLoading}
                                    >
                                        Annuler
                                    </Button>
                                </div>
                            </Form>
                        </CardBody>
                    </Card>
                </div>
            </div>
        </div>
    );
};

export default ChangePassword;
